export const S3_CONFIG = {
  /** Default bucket for all uploads (overridden by AWS_S3_BUCKET env var) */
  bucket: process.env.AWS_S3_BUCKET ?? 'eifo-lms-dev-uploads',

  /** S3 key prefixes for organized storage */
  prefixes: {
    courseThumbnails: 'courses/thumbnails/',
    lessonMedia: 'courses/lessons/media/',
    assignments: 'assignments/submissions/',
    // Grader-produced marked-up PNGs for assignment submissions. Kept under the
    // `assignments/` prefix family so any future storage-lifecycle reconciler
    // covers them alongside student submissions.
    submissionAnnotations: 'assignments/annotations/',
    // Admin-uploaded assignment answer keys. Kept in the `assignments/` family
    // for the same reason `submissionAnnotations` is. ⚠️ NEVER served through
    // /api/media/[assetId], which is UNAUTHENTICATED by design (the asset UUID
    // is the bearer token, allowlisted in middleware so `<img>` loads are not
    // bounced to /login). An answer key routed through that path is on the open
    // internet, so it must never become a `course_assets` row.
    answerKeys: 'assignments/answer-keys/',
    assessmentResponses: 'assessment-responses/',
    recordings: 'recordings/',
    avatars: 'avatars/',
    certificates: 'certificates/',
    libraryMaterials: 'library/materials/',
    bookCovers: 'reading-zone/book-covers/',
    // Reading Zone read-aloud assessment — student utterance recordings.
    // Distinct from 'reading-zone/audio/' (Feature A TTS, which deleteMaterial purges).
    readAloud: 'reading-zone/read-aloud/',
    achievementCards: 'achievements/cards/',
    socialPosts: 'social/posts/',
    aiGenerated: 'ai-generated/',
    team: 'team/photos/',
    // Teacher-inquiry contact mode — anonymous applicant credential uploads.
    teacherCredentials: 'leads/teacher-credentials/',
    // Public Blog — featured + inline post images. Served via the stable
    // /api/blog/image/[id] proxy (durable URLs, never presigned in stored HTML).
    blog: 'blog/images/',
    // Teacher onboarding packet — generated agreements, filled W-9s, and
    // degree/certification uploads (tech-spec-teacher-onboarding-step-1).
    // Per-teacher docs live under `${prefix}${onboardingId}/`; singleton admin
    // assets (signature image, base W-9) under `${prefix}settings/`.
    teacherOnboarding: 'onboarding/teacher-docs/',
    // tech-spec-communications-update-chat-enhancements — image + PDF attachments
    // on chat messages. Keyed per-conversation (`chat/attachments/{conversationId}/`)
    // so the send-time key-ownership guard can reject a foreign conversation's blob.
    chatAttachments: 'chat/attachments/',
    // Admin bug-report screenshots (tech-spec-admin-bug-reports). Keyed
    // `bug-reports/{userId}/{uuid}.{ext}` so submit can reject another admin's
    // upload by exact shape. ⚠️ NEVER served through /api/media/[assetId] —
    // only through presigned GETs that force the stored image content type.
    bugReports: 'bug-reports/',
    // Pictures embedded in a question's stem, answer options and explanation
    // (EIFO report #27). Its own prefix rather than the `courses/` family
    // because a question in the shared bank belongs to no single course —
    // see the `question_images` table comment. Served through the stable
    // /api/question-image/[id] proxy. ⚠️ Authored question content only; answer
    // keys stay under `answerKeys` and off every bearer-token path.
    questionImages: 'questions/images/',
    // Story sz-1.6 — Study Zone habitat backdrops: generated, and uploaded by an
    // administrator who already has the artwork. Keyed
    // `study-zone/backdrops/{habitatId}/{uuid}.{ext}` so
    // `completeHabitatBackdropUpload` can RE-DERIVE the key it will accept and
    // reject any other by exact shape — the same guard `bugReports` above takes,
    // and it matters more here: a caller who echoed back
    // `assignments/answer-keys/…` would otherwise publish an answer key as a
    // habitat backdrop, and sz-1.7 cuts PUBLIC delivery variants from whatever
    // this column points at.
    studyZoneBackdrops: 'study-zone/backdrops/',
    // Expedition item types, PR 3 — Kip's stored voice. Item clips are keyed
    // `study-zone/item-audio/{itemId}/{part}-{sha8}.mp3` and Kip's lines
    // `study-zone/kip-voice/{locale}/{lineKey}/{sha8}.mp3`: the hash of the
    // words in the name, so a changed text is a new object and a stale URL a
    // child already holds never plays different words. Both are made by the
    // server and never uploaded; no key crosses the wire.
    studyZoneItemAudio: 'study-zone/item-audio/',
    studyZoneKipVoice: 'study-zone/kip-voice/',
    // Expedition item types, PR 4 — a child's Say it / Answer aloud recordings,
    // keyed `study-zone/speech/{studentProfileId}/{speechAttemptId}.{ext}` so
    // the child's whole prefix can be deleted at once: consent revoked, or the
    // account deleted (`collectOwnedS3Keys('childProfile', …)`, F2). Uploaded
    // by presigned PUT to a key the server minted; served only to an admin.
    studyZoneSpeech: 'study-zone/speech/',
  },

  /** Presigned URL expiration (seconds) */
  presignedUrlExpiry: {
    upload: 600, // 10 minutes for uploads
    download: 3600, // 1 hour for downloads
  },

  /** File upload constraints */
  maxFileSize: {
    image: 10 * 1024 * 1024, // 10 MB
    video: 500 * 1024 * 1024, // 500 MB
    document: 50 * 1024 * 1024, // 50 MB
    audio: 100 * 1024 * 1024, // 100 MB
    presentation: 50 * 1024 * 1024, // 50 MB
    // Story 8.7d — `.excalidrawlib` packages are JSON files. The 50 MB document
    // budget is wildly oversized; cap at 1 MB.
    excalidrawTemplate: 1 * 1024 * 1024, // 1 MB
    // 🔴 EIFO report #207 (issue #798). This entry was MISSING, and its absence
    // was not a smaller limit — it was no limit at all.
    //
    // `getAssetTypeFromMime` resolves `application/octet-stream`,
    // `application/zip` and `application/x-zip-compressed` to the `h5p` group,
    // which is correct and deliberate: it is how `.h5p` packages get through,
    // since browsers cannot identify them. But with no entry here the lookup
    // returned `undefined`, so every `size > cap` became `size > NaN`, which is
    // always FALSE. A file of any size passed, silently, on the student
    // assignment and quiz file-answer lanes — while every screen read as though a
    // limit were in force.
    //
    // 50 MB matches the decision `content-library.service.ts ::
    // validateLibraryFileSize` already made for the same packages ("H5P packages
    // share the document budget for now"), rather than inventing a second answer
    // to the same question. That lane is deliberately left mapping to `document`:
    // changing it would move a cap that works today, which is not what this
    // report is about.
    h5p: 50 * 1024 * 1024, // 50 MB
    // Teacher-inquiry contact mode — applicant credential file (PDF/DOC/image).
    teacherCredential: 10 * 1024 * 1024, // 10 MB
  },

  /** Allowed MIME types */
  allowedMimeTypes: {
    image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    video: ['video/mp4', 'video/webm', 'video/quicktime'],
    document: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    audio: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm', 'audio/mp4', 'audio/aac'],
    presentation: [
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ],
    // Story 8.4b — `.h5p` packages are renamed ZIPs. Browsers report them as
    // `application/zip` or (on some platforms) `application/octet-stream`
    // depending on the local mime database. The extraction worker dispatches
    // by filename extension after upload — see `confirmLibraryUpload`.
    h5p: ['application/zip', 'application/octet-stream', 'application/x-zip-compressed'],
    // Story 8.7d — `.excalidrawlib` is plain JSON. Browsers usually report
    // `application/json`, occasionally `application/octet-stream` depending
    // on the local mime database. Dispatch by filename extension — see
    // `confirmLibraryUpload`.
    excalidrawTemplate: ['application/json', 'application/octet-stream'],
  },
  /** SSE config — only use when talking to real AWS S3, not MinIO */
  get serverSideEncryption(): 'AES256' | undefined {
    return process.env.AWS_S3_ENDPOINT ? undefined : 'AES256';
  },
} as const;

/**
 * Prefix for the periodic live-class thumbnail frames written by the Web
 * Egress image output (admin Live Classes snapshot grid). Nested UNDER the
 * session's recording folder so recording retention/cleanup sweeps them up
 * with the MP4. Lands in the LiveKit recording bucket (same as the MP4).
 */
export function liveSnapshotPrefix(liveSessionId: string): string {
  return `${S3_CONFIG.prefixes.recordings}${liveSessionId}/snapshots/`;
}
